import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { formatDate, deadlineLabel } from "@/components/shared";
import { FolderKanban, Inbox, Files, CheckCircle2, Clock, MessageSquare } from "lucide-react";
import { MessageThread } from "@/components/ProjectMessages";

const KPI = ({ icon: Icon, label, value, tid }) => (
  <div className="border border-slate-200 bg-white p-5 flex items-start justify-between" data-testid={tid}>
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="font-heading text-4xl font-extrabold tracking-tighter mt-2">{value}</div>
    </div>
    <div className="bg-slate-100 p-1.5 text-slate-600"><Icon size={14} /></div>
  </div>
);

export default function CustomerDashboardPage() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/customer/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="text-slate-500 text-sm">Yükleniyor...</p>;

  return (
    <div className="space-y-8 fade-in">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Müşteri Paneli</div>
        <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-1" data-testid="customer-dashboard-title">Dashboard</h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={FolderKanban} label="Projelerim" value={data.total_projects} tid="ckpi-projects" />
        <KPI icon={Inbox} label="Aktif Taleplerim" value={data.active_requests} tid="ckpi-active-requests" />
        <KPI icon={Files} label="Gelen Teklifler" value={data.total_offers} tid="ckpi-offers" />
        <KPI icon={CheckCircle2} label="Onaylanan" value={data.accepted_offers} tid="ckpi-accepted" />
      </div>

      {data.unread_messages > 0 && (
        <button
          onClick={() => navigate("/musteri/projeler")}
          data-testid="customer-unread-banner"
          className="w-full text-left border border-blue-500/30 bg-blue-500/5 p-4 flex items-center gap-3 hover:bg-blue-500/10 transition-colors duration-200"
        >
          <MessageSquare size={16} className="text-blue-600" />
          <span className="text-sm">Ekibimizden <strong>{data.unread_messages} okunmamış mesajınız</strong> var — projelerinizi açıp görüntüleyin.</span>
        </button>
      )}

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <div className="border border-slate-200 bg-white">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
            <Clock size={15} className="text-slate-500" />
            <span className="text-xs uppercase tracking-wider text-slate-500">Yaklaşan Son Tarihler</span>
          </div>
          {data.upcoming_deadlines.length === 0 ? (
            <p className="px-5 py-8 text-sm text-slate-500" data-testid="customer-no-upcoming">Aktif teklif talebiniz yok. "Teklif Al" ile yeni talep oluşturabilirsiniz.</p>
          ) : (
            data.upcoming_deadlines.map((r) => (
              <button
                key={r.id}
                onClick={() => navigate(`/musteri/teklifler/${r.id}`)}
                data-testid={`customer-upcoming-${r.id}`}
                className="w-full text-left px-5 py-3.5 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors duration-200 flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{r.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{r.offer_count}/{r.supplier_count} teklif geldi</div>
                </div>
                <div className="text-right shrink-0">
                  <Badge variant="outline" className={`rounded-none text-xs ${r.days_left <= 1 ? "bg-red-500/10 text-red-500 border-red-500/20" : "bg-yellow-500/10 text-yellow-600 border-yellow-500/20"}`}>
                    {r.days_left === 0 ? "Son Gün" : `Son ${r.days_left + 1} gün`}
                  </Badge>
                  <div className="text-xs text-slate-500 mt-1">{deadlineLabel(r)}</div>
                </div>
              </button>
            ))
          )}
        </div>

        <div data-testid="customer-direct-messages">
          <MessageThread
            endpoint="/customer/messages"
            myRole="customer"
            title="GettProposal Ekibi ile Mesajlaşma"
            emptyText="Henüz mesaj yok. Bir sorunuz varsa buradan doğrudan ekibimize yazabilirsiniz."
            placeholder="Ekibimize mesajınızı yazın..."
          />
        </div>
      </div>
    </div>
  );
}
